import React, { useState } from 'react'
import { Head, Link, router } from '@inertiajs/react'
import {
  Container, Typography, Box, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, TextField, Stack, IconButton,
  Autocomplete
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'

interface Company {
  id: number
  name: string
  country: string | null
  group_count: number
  created_at: string
}

interface SearchableCompany {
  id: number
  name: string
}

interface Props {
  companies: Company[]
  all_companies: SearchableCompany[]
  filters: { q: string | null; ids: number[] | null }
}

export default function CompaniesIndex({ companies, all_companies, filters }: Props) {
  const initialSelectedIds = (filters.ids || []).map(Number)
  const [selectedCompanies, setSelectedCompanies] = useState<SearchableCompany[]>(
    all_companies.filter((c) => initialSelectedIds.includes(c.id))
  )

  const handleSelectionChange = (_: unknown, value: SearchableCompany[]) => {
    setSelectedCompanies(value)
    const params: Record<string, unknown> = { ids: value.map((c) => c.id) }
    if ((params.ids as number[]).length === 0) delete params.ids
    router.get('/companies', params as Record<string, string>, { preserveState: true })
  }

  const handleDelete = (company: Company) => {
    if (confirm(`Are you sure you want to delete "${company.name}"? This will also delete all its groups.`)) {
      router.delete(`/companies/${company.id}`)
    }
  }

  return (
    <>
      <Head title="Companies" />
      <Container maxWidth="xl" sx={{ py: { xs: 3, md: 4 }, px: { xs: 2, md: 4 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography variant="h5">Companies</Typography>
          <Button
            component={Link}
            href="/companies/new"
            variant="contained"
            startIcon={<AddIcon />}
          >
            Add Company
          </Button>
        </Box>

        <Stack direction="row" spacing={2} sx={{ mb: 4 }}>
          <Autocomplete
            multiple
            size="small"
            options={all_companies}
            getOptionLabel={(option) => option.name}
            value={selectedCompanies}
            onChange={handleSelectionChange}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search Companies"
                placeholder="Type to search…"
              />
            )}
            sx={{ minWidth: 320, flexGrow: 1, maxWidth: 600 }}
          />
        </Stack>

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Country</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Groups</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', width: 100 }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {companies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      No companies found. Add your first company!
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                companies.map((company) => (
                  <TableRow key={company.id} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                    <TableCell>
                      <Typography
                        variant="body2"
                        component={Link}
                        href={`/companies/${company.id}`}
                        sx={{ fontWeight: 500, color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                      >
                        {company.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {company.country || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {company.group_count}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        <IconButton
                          component={Link}
                          href={`/companies/${company.id}/edit`}
                          size="small"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          aria-label="delete"
                          onClick={() => handleDelete(company)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>
    </>
  )
}
